import { router } from '@inertiajs/react'
import { Mail, RefreshCw, CheckCircle2, Clock } from 'lucide-react'
import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { DnsRecordsTable } from '@/components/settings/DnsRecordsTable'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import type EmailDomainConfiguration from '@/types/serializers/EmailDomainConfiguration'

interface EmailDomainSettingsProps {
  emailDomain: EmailDomainConfiguration | null
}

export function EmailDomainSettings({ emailDomain }: EmailDomainSettingsProps) {
  if (!emailDomain) return <SetupForm />
  if (emailDomain.verification_status === 'success') return <VerifiedState config={emailDomain} />
  return <PendingState config={emailDomain} />
}

function SetupForm() {
  const { withAccount } = useRouteParams()
  const [domain, setDomain] = useState('')
  const [fromName, setFromName] = useState('')
  const [fromAddress, setFromAddress] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    router.post(routes.settings.createEmailDomain.path(withAccount()), {
      email_domain: { domain, from_name: fromName, from_address: fromAddress }
    }, {
      preserveScroll: true,
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="size-5" />
          Custom Email Domain
        </CardTitle>
        <CardDescription>
          Send conversation emails from your own domain instead of noreply@banshee.chat.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="domain">Domain</Label>
            <Input id="domain" placeholder="acme.com" value={domain} onChange={e => setDomain(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="from_name">Sender Name</Label>
            <Input id="from_name" placeholder="Acme Support" value={fromName} onChange={e => setFromName(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="from_address">From Address</Label>
            <Input id="from_address" type="email" placeholder="support@acme.com" value={fromAddress} onChange={e => setFromAddress(e.target.value)} required />
          </div>
          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={submitting} className="cursor-pointer">
              {submitting ? 'Verifying...' : 'Verify Domain'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

function PendingState({ config }: { config: EmailDomainConfiguration }) {
  const { withAccount } = useRouteParams()
  const [checking, setChecking] = useState(false)

  const handleCheck = () => {
    setChecking(true)
    router.post(routes.settings.checkEmailDomain.path(withAccount()), {}, {
      preserveScroll: true,
      onFinish: () => setChecking(false),
    })
  }

  const handleRemove = () => {
    router.delete(routes.settings.destroyEmailDomain.path(withAccount()), {
      preserveScroll: true,
    })
  }

  const requiredRecords = config.dns_records.filter(r => r.required)
  const recommendedRecords = config.dns_records.filter(r => !r.required)
  const hasChecked = !!config.dns_checked_at

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Mail className="size-5" />
            Custom Email Domain
          </CardTitle>
          <Badge variant="outline" className="gap-1">
            <Clock className="size-3" />
            Pending verification
          </Badge>
        </div>
        <CardDescription>
          {config.domain} &mdash; From: {config.from_name} &lt;{config.from_address}&gt;
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Add these DNS records to your domain provider:
        </p>

        <DnsRecordsTable records={requiredRecords} label="Required" checked={hasChecked} />
        {recommendedRecords.length > 0 && (
          <DnsRecordsTable records={recommendedRecords} label="Recommended for deliverability" checked={hasChecked} />
        )}

        <div className="rounded-md bg-muted/50 p-3 space-y-1">
          <p className="text-sm text-muted-foreground">
            DNS changes usually take effect within <strong className="text-foreground">minutes to a few hours</strong>, but can take up to 72 hours in rare cases.
            We're checking automatically in the background.
          </p>
          <p className="text-xs text-muted-foreground">
            Started {formatElapsed(config.created_at)}
            {config.dns_checked_at && <> &middot; Last checked {formatElapsed(config.dns_checked_at)}</>}
          </p>
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button onClick={handleCheck} disabled={checking} className="cursor-pointer">
            <RefreshCw className={`size-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
            {checking ? 'Checking...' : 'Check Now'}
          </Button>
          <ConfirmDialog
            trigger={<Button variant="destructive" className="cursor-pointer">Remove Domain</Button>}
            title="Remove email domain?"
            description={`This will remove ${config.domain} and all DNS verification. Emails will revert to noreply@banshee.chat.`}
            confirmText="Remove Domain"
            onConfirm={handleRemove}
            destructive
          />
        </div>
      </CardContent>
    </Card>
  )
}

function VerifiedState({ config }: { config: EmailDomainConfiguration }) {
  const { withAccount } = useRouteParams()

  const handleRemove = () => {
    router.delete(routes.settings.destroyEmailDomain.path(withAccount()), {
      preserveScroll: true,
    })
  }

  const verifiedDate = config.verified_at
    ? new Date(config.verified_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Mail className="size-5" />
            Custom Email Domain
          </CardTitle>
          <Badge className="gap-1 bg-emerald-600">
            <CheckCircle2 className="size-3" />
            Verified
          </Badge>
        </div>
        <CardDescription>
          {config.domain} &mdash; From: {config.from_name} &lt;{config.from_address}&gt;
          {verifiedDate && <span className="block mt-1">Verified on: {verifiedDate}</span>}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Outbound emails will be sent from this domain.
        </p>
        <div className="flex justify-end">
          <ConfirmDialog
            trigger={<Button variant="destructive" className="cursor-pointer">Remove Domain</Button>}
            title="Remove email domain?"
            description={`This will remove ${config.domain}. Emails will revert to noreply@banshee.chat.`}
            confirmText="Remove Domain"
            onConfirm={handleRemove}
            destructive
          />
        </div>
      </CardContent>
    </Card>
  )
}

function formatElapsed(isoDate: string): string {
  const seconds = Math.floor((Date.now() - new Date(isoDate).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}
